/**
 * The chrome around every ops screen: the nav when the caller is entitled to
 * operate the platform, one refusing sentence when they are not.
 *
 * Access control is not decided here (platform design §3): `GET
 * /ops/tenants` already refuses with 403 for anyone the API does not
 * recognise as an operator, and this shell reads that answer through
 * `useOpsEntitlement` rather than deciding a second time from anything
 * client-side. What lives here is only the choice of what to show while
 * that answer is unclear or negative. Signed out is the one negative case
 * with an action: this plane is its own origin, so no session from the
 * tenant site ever reaches it, and without a sign-in here an operator could
 * never become entitled. A refusal and a dropped request share one sentence
 * (`NotAnOperator`); telling them apart the way `src/app/shell/Forbidden.tsx`
 * does would be exactly the reuse the bundle-boundary test
 * (`src/ops/__tests__/boundary.test.ts`) forbids.
 */

import type { ReactNode } from 'react';
import { useMatchRoute } from '@tanstack/react-router';

import { Shell, UserFooter } from '../../chrome/shell';
import { activeEntry, matchesRoute } from '../../chrome/shell/topbar';
import { defineCatalogue, t } from '../../i18n';
import { useAuthState, useSignOut } from '../../app/session/authState';
import { firebaseDisplayName, resolveDisplayName } from '../../app/session/identity';
import { opsNav } from '../routes';
import { useOpsEntitlement } from '../routes/useOpsEntitlement';
import { NotAnOperator } from './NotAnOperator';
import { OpsNav } from './OpsNav';
import { OpsSignIn } from './OpsSignIn';
import { TopbarActionsSlot } from './TopbarActions';

// Figma 153:342: a second, uppercase line under "SocialSrc" naming this as
// the operator plane, distinct from the tenant site's own brand block.
const catalogue = defineCatalogue({ eyebrow: 'OPERAÇÃO' }, { eyebrow: 'OPERATION' });

/**
 * The entitled render, its own component so `useMatchRoute` is invoked only
 * once entitlement is confirmed -- `OpsShell`'s other branches render before
 * a router is guaranteed to be mounted around them, e.g. a refusal shown
 * mid-boot.
 */
function OpsShellEntitled(props: {
  name: string | null;
  onSignOut: () => void;
  children: ReactNode;
}): ReactNode {
  const matchRoute = useMatchRoute();
  const active = activeEntry(
    (entry) => matchesRoute(matchRoute, entry),
    opsNav.map((entry) => ({ path: entry.path, label: entry.label() })),
  );

  return (
    <Shell
      brand={{ label: t('shell.appName'), href: '/' }}
      switcher={
        <p className="text-eyebrow text-muted-foreground -mt-1 px-2 uppercase">
          {catalogue.t('eyebrow')}
        </p>
      }
      nav={<OpsNav entries={opsNav} />}
      footer={<UserFooter name={props.name} onSignOut={props.onSignOut} />}
      topbar={{
        // No organisation or campaign above an ops screen: the trail is the
        // screen, and whatever a page below it names with `useBreadcrumbLeaf`.
        crumbs: active === null ? [] : [{ label: active.label, href: active.path }],
        exact: active !== null && matchesRoute(matchRoute, active, { exact: true }),
        actions: <TopbarActionsSlot />,
      }}
    >
      {props.children}
    </Shell>
  );
}

export function OpsShell(props: { children: ReactNode }): ReactNode {
  const entitlement = useOpsEntitlement();
  const session = useAuthState();
  const signOut = useSignOut();

  if (entitlement.status === 'checking') {
    return (
      <p role="status" className="text-muted-foreground text-body-sm p-8">
        {t('shell.loading')}
      </p>
    );
  }

  if (entitlement.status === 'signed-out') {
    return <OpsSignIn />;
  }

  if (entitlement.status !== 'entitled') {
    return <NotAnOperator />;
  }

  const name = resolveDisplayName(firebaseDisplayName(), session.user?.email ?? null);

  return (
    <OpsShellEntitled name={name} onSignOut={() => void signOut()}>
      {props.children}
    </OpsShellEntitled>
  );
}
